import { Box, Button, Typography } from '@mui/material';
import { useGetContentPosts } from 'api/services/SupplierPortal/ContentManagementService';
import MyCarousel from 'components/common/Carousel/Carousel';
import { staticContent } from 'components/constants/Constants';
import {
  ContentPost,
  ContentPostData_MeetUs,
  UXBContentCategory
} from 'portals/types';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { UXB_EVENT_KEY } from 'supplier-portals/uxbeauty/constants';
import { UXB_ROUTE_NAMES } from 'supplier-portals/uxbeauty/UXBRouteNames';
import { tss } from 'tss-react/mui';
import { prepareMeetUsPageData } from './prepareMeetUsPageData';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { captureException } from '@sentry/react';

const MeetUsPage = () => {
  const { classes } = useStyles();
  const [result, fetchContentPosts] =
    useGetContentPosts<ContentPostData_MeetUs>(
      UXB_EVENT_KEY,
      UXBContentCategory.MEET_US
    );

  useEffect(() => {
    fetchContentPosts({ params: { size: 200 } });
  }, [fetchContentPosts]);

  useEffect(() => {
    if (result.data && result.data.page.total_elements > 190) {
      captureException(
        new Error(
          'MeetUsPage: More than 190 events found. Please check if it is still usable without performance issues.'
        )
      );
    }
  }, [result.data]);

  useEffect(() => {
    if (result.error) toast.error('Failed to load events');
  }, [result.error]);

  const data = result.data
    ? prepareMeetUsPageData(result.data.content)
    : undefined;
  const selectedEvent = data?.currentEvent;
  const previousEvents = data?.pastEvents;

  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 3
    },
    desktopLarger: {
      breakpoint: { max: 3000, min: 2000 },
      items: 3
    },
    desktop: {
      breakpoint: { max: 2000, min: 1200 },
      items: 2
    },
    tablet: {
      breakpoint: { max: 1200, min: 464 },
      items: 1
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1
    }
  };

  return (
    <>
      <Helmet>
        <title>Meet us</title>
      </Helmet>
      <Box
        sx={{
          py: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundImage: `url(${staticContent}/${
            selectedEvent?.image ??
            'uxb/backgrounds/meetus/PCHi_China_2025.webp'
          })`,
          backgroundSize: 'cover',
          backgroundPositionY: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <Typography
          variant="h2"
          sx={{
            mb: 2,
            color: 'white'
          }}
        >
          Our events
        </Typography>
        <Box
          sx={{
            background: 'white',
            padding: '48px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: 'fit-content',
            maxWidth: '80vw'
          }}
        >
          <Typography sx={{ textTransform: 'uppercase' }} variant="h5">
            Trade Show
          </Typography>

          <Typography
            variant="h2"
            sx={{ mb: 8, textAlign: 'center' }}
            className={classes.customH2}
          >
            {selectedEvent?.data.title}
          </Typography>
          <Button
            variant="contained"
            component={Link}
            to={`/webinar/${selectedEvent?.key}`}
          >
            DISCOVER
          </Button>
        </Box>
      </Box>
      <Box
        style={{
          padding: '100px 0',
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: `url(${staticContent}/uxb/backgrounds/d572fc86ba1702daccb08af5edb7de41.jpg)`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPositionY: 'center'
        }}
      >
        <Typography
          variant="h2"
          sx={{
            mb: 2,
            color: 'white',
            textAlign: 'left',
            marginLeft: '10vw'
          }}
        >
          OUR PREVIOUS EVENTS
        </Typography>
        <div
          style={{
            width: '80vw',
            height: 'max-content',
            position: 'relative',
            margin: 'auto'
          }}
        >
          <MyCarousel responsive={responsive} partialVisible={true}>
            {[
              ...(previousEvents ?? []).map(event => (
                <PreviewEventCard key={event.data.title} content={event} />
              )),
              <OldPreviewEventCard
                key="specialCase"
                content={{
                  img: `${staticContent}/uxb/backgrounds/f1bd51804a7364e0132c68a3d3f85671.jpeg`,
                  title:
                    "Let's catch up on Biotech for Beauty with Givaudan experts!",
                  button: {
                    label: 'MEET US',
                    link: 'https://assets.mediafly.com/shares/e6bf5201684247c683d01e167a8a759fproduct512724/8fada'
                  }
                }}
              />
            ]}
          </MyCarousel>
        </div>
      </Box>
    </>
  );
};

export default MeetUsPage;
const useStyles = tss.create(({ theme }) => ({
  root: {
    backgroundColor: 'white',
    margin: '0 10px',
    display: 'flex',
    height: '100%',
    img: {
      width: 200,
      objectFit: 'cover'
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      img: {
        width: '100%',
        height: '250px'
      }
    }
  },
  textSideWrapper: {
    padding: '36px'
  },
  customH2: {
    marginTop: theme.spacing(2),
    color: theme.palette.secondary.main
  }
}));
const PreviewEventCard = ({
  content
}: {
  content: ContentPost<ContentPostData_MeetUs>;
}) => {
  const { classes } = useStyles();
  return (
    <Box
      className={classes.root}
      component={Link}
      to={`${UXB_ROUTE_NAMES.webinar}/${content?.key}`}
    >
      <img src={staticContent + '/' + content.image} alt={content.data.title} />
      <Box className={classes.textSideWrapper}>
        <Typography variant="h5">TRADE SHOW</Typography>
        <Typography variant="h2" className={classes.customH2}>
          {content.data.title}
        </Typography>
      </Box>
    </Box>
  );
};

interface PreviewEventCardProps {
  title: string;
  img: string;
  button?: {
    label: string;
    link: string;
  };
}
const OldPreviewEventCard = ({
  content
}: {
  content: PreviewEventCardProps;
}) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.root}>
      <img src={content.img} alt={content.title} />
      <Box className={classes.textSideWrapper}>
        <Typography variant="h5">TRADE SHOW</Typography>
        <Typography variant="h2" className={classes.customH2}>
          {content.title}
        </Typography>
        {content.button && (
          <Button
            component={Link}
            to={content.button.link}
            target="_blank"
            variant="contained"
            sx={{ mt: 3 }}
          >
            {content.button.label}
          </Button>
        )}
      </Box>
    </Box>
  );
};
